import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import {
  ASSET_CATEGORIES,
  DEBT_CATEGORIES,
  ACCOUNT_TYPES,
} from "@/shared/constants/estateCategories";
import type { EstateItem } from "@/shared/types/estateTypes";
import {
  getNameLabel,
  getTypeLabel,
  getValueLabel,
  getPlaceholder,
} from "@/shared/utils/estateItemLabels";
import { resolveBalance } from "@/shared/utils/balanceEstimates";
import { currencyInput } from "@/shared/utils/currencyInput";
import { BalanceEstimateSection } from "./BalanceEstimateSection";
import { enqueueSnackbar } from "notistack";
import { AIAssetEstimateForm } from "./AIAssetEstimateForm";
import { Sparkles } from "lucide-react";

const MARKED_AS_NONE = "__MARKED_AS_NONE__";
const VEHICLE_NAME_PATTERN = /^(\d{4})\s+(\S+)\s+(.+)$/;
const VALUE_HIDDEN_CATEGORIES = new Set([
  "LLC or partnership interest",
  "Stock in a private company",
  "Sole proprietorship",
]);

function parseVehicleMetadata(name: string): Record<string, unknown> | undefined {
  const match = name.match(VEHICLE_NAME_PATTERN);
  if (!match) return undefined;
  const [, year, make, model] = match;
  return { year: parseInt(year), make, model };
}

const allCategories = [...ASSET_CATEGORIES, ...DEBT_CATEGORIES];

interface AddEstateItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  estateId: number;
  initialCategory?: string;
  enableAIValuation?: boolean;
  pendingItem?: EstateItem;
}

export function AddEstateItemModal({
  isOpen,
  onClose,
  onSuccess,
  estateId,
  initialCategory,
  enableAIValuation = false,
  pendingItem,
}: AddEstateItemModalProps) {
  const { addItem, updateItem, approveItem, assets, debts, deleteItem } = useEstateItems(estateId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [mode, setMode] = useState<"ai" | "manual">("manual");

  const [itemName, setItemName] = useState("");
  const [category, setCategory] = useState("");
  const [itemType, setItemType] = useState("");
  const [institution, setInstitution] = useState("");
  const [value, setValue] = useState("");
  const [balanceUnknown, setBalanceUnknown] = useState(false);
  const [estimateRange, setEstimateRange] = useState("");

  const INSTITUTION_CATEGORIES = new Set([
    "Bank accounts",
    "Brokerage accounts",
    "Tax-advantaged savings",
    "Retirement accounts",
    "Unsecured Debts",
  ]);

  useEffect(() => {
    if (isOpen && pendingItem) {
      setItemName(pendingItem.item_name || "");
      setCategory(pendingItem.category || "");
      setItemType(pendingItem.item_type || "");
      setInstitution((pendingItem.metadata?.institution as string) || "");
      setValue(pendingItem.balance ? String(pendingItem.balance) : "");
      setMode("manual");
    } else if (isOpen && initialCategory) {
      setCategory(initialCategory);
      const supportsAI = initialCategory === "Properties" || initialCategory === "Vehicles";
      setMode(enableAIValuation && supportsAI ? "ai" : "manual");
    }
  }, [isOpen, pendingItem, initialCategory, enableAIValuation]);

  useEffect(() => {
    if (!isOpen) {
      setItemName("");
      setCategory("");
      setItemType("");
      setInstitution("");
      setValue("");
      setBalanceUnknown(false);
      setEstimateRange("");
      setMode("manual");
    }
  }, [isOpen]);

  const classification = (ASSET_CATEGORIES as readonly string[]).includes(category)
    ? "asset"
    : "debt";

  const categoryLabel = "Category";
  const itemTypes = category ? ACCOUNT_TYPES[category] || [] : [];

  const hideValue = VALUE_HIDDEN_CATEGORIES.has(category);
  const hasValue =
    hideValue || (balanceUnknown ? !!estimateRange : !!(value && parseFloat(value) > 0));
  const canSubmit = itemName.trim() && category && hasValue;

  const { handleChange: handleValueChange, displayValue } = currencyInput(value, setValue);

  const deleteNoneRecord = async () => {
    const allItems = [...assets, ...debts];
    const noneRecord = allItems.find(
      (item) => item.category === category && item.notes === MARKED_AS_NONE,
    );

    if (noneRecord?.id) {
      await deleteItem(noneRecord.id);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      const vehicleMeta = category === "Vehicles" ? parseVehicleMetadata(itemName) : undefined;
      const institutionMeta = institution.trim() ? { institution: institution.trim() } : undefined;
      const metadata =
        vehicleMeta || institutionMeta ? { ...vehicleMeta, ...institutionMeta } : undefined;
      const resolvedBalance = resolveBalance(balanceUnknown, estimateRange, value);

      if (pendingItem?.id) {
        await updateItem(pendingItem.id, {
          item_name: itemName,
          is_custom_name: true,
          category,
          item_type: itemType || undefined,
          balance: resolvedBalance,
          classification,
          is_estimated: balanceUnknown,
          balance_estimate_range: balanceUnknown ? estimateRange : null,
          ...(metadata ? { metadata: { ...pendingItem.metadata, ...metadata } } : {}),
        });
        await approveItem(pendingItem.id);
        enqueueSnackbar("Account added", { variant: "success" });
      } else {
        await deleteNoneRecord();

        await addItem({
          estate_id: estateId,
          classification,
          category,
          item_type: itemType || undefined,
          item_name: itemName,
          is_custom_name: true,
          balance: resolvedBalance,
          is_estimated: balanceUnknown,
          balance_estimate_range: balanceUnknown ? estimateRange : null,
          ...(metadata ? { metadata } : {}),
        });

        enqueueSnackbar("Item added", { variant: "success" });
      }
      onClose();
      onSuccess?.();
    } catch {
      enqueueSnackbar("Couldn't add item. Please try again.", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAIConfirm = async (data: { itemName: string; itemType?: string; value: number }) => {
    setIsSubmitting(true);
    try {
      await deleteNoneRecord();

      const aiVehicleMeta =
        category === "Vehicles" ? parseVehicleMetadata(data.itemName) : undefined;

      await addItem({
        estate_id: estateId,
        classification,
        category: category as string,
        item_type: data.itemType || undefined,
        item_name: data.itemName,
        is_custom_name: true,
        balance: data.value,
        ...(aiVehicleMeta ? { metadata: aiVehicleMeta } : {}),
      });

      enqueueSnackbar("Item added", { variant: "success" });
      onClose();
      onSuccess?.();
    } catch {
      enqueueSnackbar("Couldn't add item. Please try again.", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const supportsAI = enableAIValuation && (category === "Properties" || category === "Vehicles");
  const showAIMode = mode === "ai" && supportsAI;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>
            {pendingItem ? "Confirm account" : showAIMode ? "Smart Estimate" : "Add manually"}
          </DialogTitle>
        </DialogHeader>

        {supportsAI && (
          <div className="flex gap-2 px-6 pb-4">
            <Button
              type="button"
              variant={mode === "ai" ? "outline" : "ghost"}
              onClick={() => setMode("ai")}
              className={`flex-1 ${mode === "ai" ? "border-brand bg-brand-subtle" : ""}`}
            >
              <Sparkles className="h-4 w-4 text-brand" />
              Smart Estimate
            </Button>
            <Button
              type="button"
              variant={mode === "manual" ? "outline" : "ghost"}
              onClick={() => setMode("manual")}
              className={`flex-1 ${mode === "manual" ? "border-brand bg-brand-subtle" : ""}`}
            >
              Manual Entry
            </Button>
          </div>
        )}

        {showAIMode ? (
          <DialogBody>
            <AIAssetEstimateForm
              estateId={estateId}
              assetType={category === "Properties" ? "real-estate" : "vehicle"}
              onConfirm={handleAIConfirm}
              onCancel={onClose}
            />
          </DialogBody>
        ) : (
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <DialogBody className="space-y-5">
              <div className="space-y-2">
                <Label className="text-sm">
                  {categoryLabel}
                  <span className="text-red-600 ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Select
                  value={category}
                  onValueChange={(val) => {
                    setCategory(val);
                    setItemType("");
                    setInstitution("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {allCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {category && itemTypes.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm">
                    {getTypeLabel(category)}
                    <span className="text-red-600 ml-0.5" aria-hidden="true">
                      *
                    </span>
                  </Label>
                  <Select value={itemType} onValueChange={setItemType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {itemTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {category && INSTITUTION_CATEGORIES.has(category) && (
                <div className="space-y-2">
                  <Label className="text-sm">
                    Institution <span className="text-secondary">(optional)</span>
                  </Label>
                  <Input
                    placeholder="e.g., Chase, Fidelity, Vanguard"
                    value={institution}
                    onChange={(e) => setInstitution(e.target.value)}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm">
                  {getNameLabel(category)}
                  <span className="text-red-600 ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Input
                  placeholder={getPlaceholder(category)}
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  required
                />
              </div>

              {category && !hideValue && (itemTypes.length === 0 || itemType) && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-sm">
                      {getValueLabel(category)}
                      <span className="text-red-600 ml-0.5" aria-hidden="true">
                        *
                      </span>
                    </Label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-3 flex items-center text-sm text-secondary">
                        $
                      </span>
                      <Input
                        type="text"
                        placeholder="0.00"
                        value={displayValue}
                        onChange={handleValueChange}
                        className="pl-7 disabled:bg-subtle disabled:text-tertiary"
                        disabled={balanceUnknown}
                      />
                    </div>
                  </div>

                  <BalanceEstimateSection
                    balanceUnknown={balanceUnknown}
                    estimateRange={estimateRange}
                    onBalanceUnknownChange={(checked) => {
                      setBalanceUnknown(checked);
                      if (!checked) {
                        setEstimateRange("");
                      } else {
                        setValue("");
                      }
                    }}
                    onEstimateRangeChange={setEstimateRange}
                  />
                </div>
              )}
            </DialogBody>

            <div className="flex justify-end pt-6">
              <Button type="submit" disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? "Adding..." : "Add"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
